import { apiSlice } from './apiSlice'
import { ENDPOINTS } from '../../lib/endpoints'

export interface ContentReport {
  id:                 string
  reporterId:         string
  targetType:         'discussion' | 'comment'
  targetId:           string
  reason:             string
  status:             'pending' | 'resolved' | 'dismissed'
  resolvedAt:         string | null
  createdAt:          string
  // Enriched by the backend — avoids showing raw UUIDs in the admin UI
  contentTitle:       string | null  // discussion title (for both types)
  contentPreviewText: string | null  // discussion body snippet or comment snippet
  discussionId:       string | null  // always the parent discussion ID
  reporter: {
    id:    string
    profile: { displayName: string; avatarUrl: string | null } | null
  }
}

export interface ReportsResponse {
  data:       ContentReport[]
  nextCursor: string | null
  hasMore:    boolean
}

export const moderationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({

    getReportReasons: builder.query<{ reasons: string[] }, void>({
      query: () => ENDPOINTS.moderation.reasons,
      keepUnusedDataFor: 3600,
      providesTags: ['Report'],
    }),

    updateReportReasons: builder.mutation<{ reasons: string[] }, { reasons: string[] }>({
      query: (body) => ({
        url:    ENDPOINTS.moderation.adminReasons,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Report'],
    }),

    submitReport: builder.mutation<
      { report: ContentReport; message?: string },
      { targetType: 'discussion' | 'comment'; targetId: string; reason: string }
    >({
      query: (body) => ({
        url:    ENDPOINTS.moderation.report,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Report'],
    }),

    getAdminReports: builder.query<
      ReportsResponse,
      { status?: string; cursor?: string; limit?: number }
    >({
      query: (params) => ({ url: ENDPOINTS.moderation.adminList, params }),
      serializeQueryArgs: ({ queryArgs }) => queryArgs.status ?? 'pending',
      merge: (current, next, { arg }) => {
        if (!arg.cursor) return next
        return { ...next, data: [...current.data, ...next.data] }
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor || currentArg?.status !== previousArg?.status,
      providesTags: ['Report'],
    }),

    resolveReport: builder.mutation<
      { report: ContentReport },
      { id: string; action: 'resolve' | 'dismiss' }
    >({
      query: ({ id, action }) => ({
        url:    ENDPOINTS.moderation.adminResolve(id),
        method: 'PATCH',
        body:   { action },
      }),
      invalidatesTags: ['Report'],
    }),

  }),
})

export const {
  useGetReportReasonsQuery,
  useUpdateReportReasonsMutation,
  useSubmitReportMutation,
  useGetAdminReportsQuery,
  useResolveReportMutation,
} = moderationApi
